import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import { selectCollectionScoringV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdScoringsScoringIdSelectPost } from '../../generated';
import { toCollectionScoring } from '../../queries/list-collection-scorings';
import type { CollectionScoring } from '../../queries/list-collection-scorings';

/*
 * POST /v1/workspaces/{workspace_id}/assets/images/collections/{collection_id}/scorings/{scoring_id}/select: stamps
 * `last_selected_at` on a scored pair so the page reopens on it and the collection's `last_scoring` follows. Tests
 * answer this path with `tests/mocks/collection-scoring-handlers.ts`.
 */

export interface SelectCollectionScoringMutationVariables {
  workspace_id: string;
  collection_id: string;
  scoring_id: string;
}

export type SelectCollectionScoringMutation = CollectionScoring;

async function selectCollectionScoring(variables: SelectCollectionScoringMutationVariables) {
  const { workspace_id, collection_id, scoring_id } = variables;
  const { data } =
    await selectCollectionScoringV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdScoringsScoringIdSelectPost({
      path: { workspace_id, collection_id, scoring_id },
      throwOnError: true,
    });
  return toCollectionScoring(data);
}

export const useSelectCollectionScoringMutation = <TError = unknown, TContext = unknown>(
  options?: Omit<
    UseMutationOptions<SelectCollectionScoringMutation, TError, SelectCollectionScoringMutationVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  return useMutation<SelectCollectionScoringMutation, TError, SelectCollectionScoringMutationVariables, TContext>({
    mutationKey: ['SelectCollectionScoring'],
    mutationFn: selectCollectionScoring,
    ...options,
  });
};

useSelectCollectionScoringMutation.fetcher = (variables: SelectCollectionScoringMutationVariables) => () =>
  selectCollectionScoring(variables);
